import { defineStore } from "pinia";

/**
 * Client-side fallback when `adminExpiresAt` is missing; should match backend admin JWT TTL.
 * Build-time: set `VUE_APP_ADMIN_TOKEN_EXPIRATION_MINUTES` in `.env` / `.env.local` (minutes, positive integer).
 */
function getAdminTokenExpirationMinutes() {
  const raw = process.env.VUE_APP_ADMIN_TOKEN_EXPIRATION_MINUTES;
  const n = parseInt(String(raw ?? ""), 10);
  if (Number.isFinite(n) && n > 0 && n <= 10080) return n;
  return 240;
}

const TOKEN_EXPIRATION_MINUTES = getAdminTokenExpirationMinutes();
const STORAGE_KEYS = {
  TOKEN: "adminAccessToken",
  ADMIN_DATA: "admin_data",
  TOKEN_RECEIVED_AT: "admin_token_received_at",
  EXPIRES_AT: "adminExpiresAt",
};

function getStoredAdmin() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.ADMIN_DATA);
    if (raw == null || raw === "") return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** Valid admin JWT + role super_admin or support + not past expiry (same rules everywhere). */
function adminSessionValidForSupportTools(state) {
  if (!state.accessToken || typeof state.accessToken !== "string") return false;
  const admin = state.admin || getStoredAdmin();
  if (!admin || typeof admin.role !== "string") return false;
  const role = admin.role.trim().toLowerCase();
  if (role !== "super_admin" && role !== "support") return false;
  const now = Date.now();
  const storedExpiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
  if (storedExpiresAt != null && storedExpiresAt !== "") {
    const expiryTime = parseInt(storedExpiresAt, 10);
    if (!Number.isNaN(expiryTime) && now >= expiryTime) return false;
  }
  const receivedAt =
    state.tokenReceivedAt ||
    parseInt(localStorage.getItem(STORAGE_KEYS.TOKEN_RECEIVED_AT) || "0", 10) ||
    0;
  if (receivedAt === 0) return false;
  const expiryTime = receivedAt + TOKEN_EXPIRATION_MINUTES * 60 * 1000;
  return now < expiryTime;
}

export const useAdminAuthStore = defineStore("adminAuth", {
  state: () => ({
    accessToken: localStorage.getItem(STORAGE_KEYS.TOKEN) || null,
    admin: getStoredAdmin(),
    tokenReceivedAt: parseInt(localStorage.getItem(STORAGE_KEYS.TOKEN_RECEIVED_AT) || "0", 10) || 0,
  }),

  getters: {
    /** True if we have a token and it is not yet expired (backend EXPIRES_AT or sliding window from TOKEN_RECEIVED_AT). */
    isAuthenticated(state) {
      if (!state.accessToken || typeof state.accessToken !== "string") return false;
      const now = Date.now();
      const storedExpiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
      if (storedExpiresAt != null && storedExpiresAt !== "") {
        const expiryTime = parseInt(storedExpiresAt, 10);
        if (!Number.isNaN(expiryTime)) {
          return now < expiryTime;
        }
      }
      const receivedAt = state.tokenReceivedAt || 0;
      if (receivedAt === 0) return false;
      return now < receivedAt + TOKEN_EXPIRATION_MINUTES * 60 * 1000;
    },

    /**
     * True if admin is logged in and has role super_admin or support (can manage orders).
     * Used by orders.vue and route guards for order management.
     */
    canManageOrders(state) {
      return adminSessionValidForSupportTools(state);
    },

    /**
     * Seller management, global listings, seller reviews moderation (backend: super_admin or support).
     * Alias of canManageOrders — same session + role rules.
     */
    canAccessSupportAdminTools(state) {
      return adminSessionValidForSupportTools(state);
    },
  },

  actions: {
    /**
     * Call after successful admin login-verify. Persists token and optional admin payload
     * in state and localStorage for use by API calls (e.g. Authorization: Bearer <token>).
     * @param {string} token - JWT from backend (data.access_token)
     * @param {object|null} admin - { email, role, admin_id } from backend
     * @param {number|null} expiresAt - Expiry time in ms (e.g. new Date(data.expires_in).getTime())
     */
    setAuthData(token, admin = null, expiresAt = null) {
      if (!token || typeof token !== "string") return;
      const trimmed = token.trim();
      if (!trimmed) return;

      this.accessToken = trimmed;
      this.tokenReceivedAt = Date.now();
      localStorage.setItem(STORAGE_KEYS.TOKEN, trimmed);
      localStorage.setItem(STORAGE_KEYS.TOKEN_RECEIVED_AT, String(this.tokenReceivedAt));

      if (expiresAt != null && !Number.isNaN(Number(expiresAt))) {
        localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, String(expiresAt));
      }

      if (admin && typeof admin === "object") {
        this.admin = { ...admin };
        localStorage.setItem(STORAGE_KEYS.ADMIN_DATA, JSON.stringify(this.admin));
      }
    },

    /**
     * Call on app load and before admin route guards. Clears stored auth if token
     * is missing or expired so isAuthenticated stays correct. Syncs state from
     * localStorage when token exists there but store was not hydrated (e.g. refresh).
     */
    checkToken() {
      const fromStorage = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const token = this.accessToken || fromStorage;
      if (!token || typeof token !== "string") {
        this.logout();
        return false;
      }
      if (!this.accessToken && fromStorage) {
        this.accessToken = fromStorage;
        this.tokenReceivedAt = parseInt(localStorage.getItem(STORAGE_KEYS.TOKEN_RECEIVED_AT) || "0", 10) || 0;
        const admin = getStoredAdmin();
        if (admin) this.admin = admin;
      }

      const now = Date.now();
      const expiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
      if (expiresAt != null && expiresAt !== "") {
        const expiryTime = parseInt(expiresAt, 10);
        if (!Number.isNaN(expiryTime) && now >= expiryTime) {
          this.logout();
          return false;
        }
        if (!Number.isNaN(expiryTime) && now < expiryTime) return true;
      }

      const receivedAt = this.tokenReceivedAt || parseInt(localStorage.getItem(STORAGE_KEYS.TOKEN_RECEIVED_AT) || "0", 10) || 0;
      if (receivedAt === 0) {
        this.logout();
        return false;
      }
      const expiryTime = receivedAt + TOKEN_EXPIRATION_MINUTES * 60 * 1000;
      if (now >= expiryTime) {
        this.logout();
        return false;
      }
      return true;
    },

    logout() {
      this.accessToken = null;
      this.admin = null;
      this.tokenReceivedAt = 0;
      Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
    },

    updateAdmin(data) {
      if (!this.admin || typeof data !== "object") return;
      this.admin = { ...this.admin, ...data };
      localStorage.setItem(STORAGE_KEYS.ADMIN_DATA, JSON.stringify(this.admin));
    },
  },
});
